/* eslint-disable */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Reorder from 'react-reorder';

// actions
import {
  setPlacesAddress,
  setFocusedPlaces,
  setPlace,
  reorderPlacesFields,
  editPlaces,
  erasePlace,
} from '../../reducers/Places/actions';
import { updateAutocompleteInput } from '../../reducers/Autocomplete/actions';
import { updateSuperchargersRoutechargers } from '../../reducers/Superchargers/actions';
import { updateAppComponentHeight } from '../../reducers/App/actions';

// components
import PlaceInput from '../../components/Places';

// containers
import Autocomplete from '../Autocomplete';
import ClearBtn from '../ClearBtn';

// helpers
import MapUtils from '../../lib/MapUtils';
import MapMarkers from '../../lib/MapMarkers';
import MapWindow from '../../lib/MapWindow';
import PlaceUtils from '../../lib/PlaceUtils';
import AutocompleteUtils from '../../lib/AutocompleteUtils';
import { i18n } from '../../lib/Utils';

import { PLACES } from '../../lib/Constants';

const PlacesPropTypes = {
  dispatchSetPlacesAddress: PropTypes.func.isRequired, // Dispatches address to to Redux store.
  mapLoaded: PropTypes.bool.isRequired, // Lets the container know that the Map has loaded.
  fields: PropTypes.arrayOf(PropTypes.object).isRequired, // Contains objects with information about each input field & location
  children: PropTypes.node, // Child nodes passed to Places container
  isMobile: PropTypes.bool.isRequired, // True when the app is loaded on a handheld sized device
  directionsLoaded: PropTypes.bool.isRequired, // If the directions have successfully returned from API
  dispatchUpdateAutocompleteInput: PropTypes.func.isRequired, // Passes input value to the rest of app
  dispatchSetPlacesFocused: PropTypes.func.isRequired, // Dispatches currently focused input field
  dispatchSetPlace: PropTypes.func.isRequired, // Handles setting origin, stopover, or destination
  dispatchReorderPlacesFields: PropTypes.func.isRequired, // Reordered the places fields
  currentState: PropTypes.shape({}).isRequired, // The app state before user began editing their trip details
  editing: PropTypes.bool.isRequired, // If the route is being edited
  fieldsLength: PropTypes.number.isRequired, // # of places in the users trip
  dispatchEditPlaces: PropTypes.func.isRequired, // Captures current state and toggles edit mode on
  stopoversSingular: PropTypes.string.isRequired, // Localized label for # of stopovers "1 stop"
  stopoversPlural: PropTypes.string.isRequired, // Localized label for # of stopovers "2 stops"
  dispatchErasePlace: PropTypes.func.isRequired, // Erases details related to the emptied field
  superchargers: PropTypes.arrayOf(PropTypes.object).isRequired, // Network of superchargers
  dispatchUpdateSuperchargersRoutechargers: PropTypes.func.isRequired, // Removes chargers from route
  showDestinationChargers: PropTypes.bool.isRequired, // True if destination chargers are displayed
  animationName: PropTypes.string.isRequired, // Name of the animation applied to elements
  translateX: PropTypes.string.isRequired, // translateX CSS property for animation
  animationTimeout: PropTypes.number.isRequired, // duration of animation
  routechargers: PropTypes.arrayOf(PropTypes.object).isRequired, // Chargers along a users plotted trip
  dispatchComponentHeight: PropTypes.func.isRequired, // update component heights
};

const PlacesDefaultProps = {
  children: null,
  shortAddres: '',
};

class Places extends Component {
  constructor(props) {
    super(props);

    this.onInputChange = this.onInputChange.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onReorder = this.onReorder.bind(this);
    this.toggleEditMode = this.toggleEditMode.bind(this);
    this.handleBlur = this.handleBlur.bind(this);

    this.state = {
      geocoderMounted: false,
      keyPressed: '',
    };
  }

  componentDidUpdate() {
    const { mapLoaded, dispatchComponentHeight } = this.props;
    const { geocoderMounted } = this.state;

    if (mapLoaded && !geocoderMounted) {
      this.initGeocoder();
      dispatchComponentHeight(PLACES, this.component.offsetHeight);
    }
  }

  onFocus(e) {
    this.props.dispatchSetPlacesFocused(e.target.name);
  }

  /*
   * @description If the user did not use the autocomplete, we should try to guess their address
   *              This is more of a band-aid to fix a design flaw where the autocomplete is hidden
   *              behind the virtual keyboard.  New users will not know that it is there.
   */
  onBlur(e) {
    e.persist();

    setTimeout(this.handleBlur.bind(null, e), 2000);
  }

  onInputChange(e) {
    const { superchargers, isMobile, routechargers } = this.props;

    const field = e.target;
    this.setAddress(field);

    // if the user erases the field then remove polyline and markers
    if (field.value === '' || field.value.length <= 1) {
      this.props.dispatchErasePlace(field);
      this.props.dispatchUpdateSuperchargersRoutechargers([]);
      MapMarkers.updateChargerIconSize(superchargers);
      MapWindow.closeInfoWindow(isMobile);
      MapMarkers.deleteMapMarkers(routechargers);
    }
  }

  onKeyPress(e) {
    const key = e.key;
    if (
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'Enter' ||
      key === 'Escape'
    ) {
      this.setState({ keyPressed: key }, () => {
        this.setState({ keyPressed: '' });
      });
    }
  }

  onReorder(e, previousIndex, nextIndex) {
    const field = e.target;
    this.props.dispatchReorderPlacesFields(field, previousIndex, nextIndex);
  }

  setAddress(field) {
    this.props.dispatchUpdateAutocompleteInput(field.value);
    this.props.dispatchSetPlacesAddress(field, field.value);
  }

  toggleEditMode() {
    const { currentState, editing } = this.props;

    if (!editing) {
      this.props.dispatchEditPlaces(currentState);
    }
  }

  handleBlur(e) {
    const { fieldsLength, fields } = this.props;
    const targetValue = e.target.value;
    const targetId = e.target.id;
    const activeElement = document.activeElement;
    const target = e.target;
    if (
      targetValue &&
      fields[Number(targetId)].lat === 0 &&
      activeElement !== target
    ) {
      AutocompleteUtils.getPlace(
        fieldsLength - 1,
        this.props.dispatchSetPlace,
        e.target,
      );
      AutocompleteUtils.clearPredictions();
    }
  }

  initGeocoder() {
    const google = window.google;
    const map = window.map;
    const { isMobile } = this.props;

    this.geocoder = MapUtils.getGeocoder();

    // let the container know that the geocoder service has been instantiated
    this.mountGeoCoder();

    google.maps.event.addListener(map, 'click', event => {
      if (event.placeId) {
        event.stop();
      }

      MapWindow.closeInfoWindow(isMobile);

      this.geocoder.geocode({ latLng: event.latLng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0]) {
            const place = results[0];
            const field = PlaceUtils.setFirstEmptyField(
              place.formatted_address,
            );
            const { fieldsLength } = this.props;
            if (field)
              this.props.dispatchSetPlace(field, place, fieldsLength - 1);
          }
        }
      });
    });
  }

  mountGeoCoder() {
    this.setState(prevState => ({
      ...prevState,
      geocoderMounted: true,
    }));
  }

  renderInputs() {
    const {
      fields,
      editing,
      stopoversSingular,
      stopoversPlural,
      fieldsLength,
    } = this.props;

    const fieldsLen = fieldsLength - 1;

    const inputs = fields.map((field, index) => {
      // set mobile input labels
      let value = field.address;
      let shortAddress = field.shortAddress;

      if (!editing && fieldsLen > 1 && index > 0 && index < fieldsLen) {
        const stopOversLabel =
          fieldsLen - 1 > 1 ? stopoversPlural : stopoversSingular;
        value = i18n(stopOversLabel, { TOTAL: fieldsLen - 1 });
        shortAddress = i18n(stopOversLabel, { TOTAL: fieldsLen - 1 });
      }

      // hide input fields when a user is not editing their trip
      const hide = !!(!editing && index > 1 && index < fieldsLen);

      return (
        <div key={field.id} className={classNames({ hidden: hide })}>
          <PlaceInput
            glyph="circle-o"
            renderAutoComplete={this.renderAutoComplete}
            defaultText={field.defaultText}
            name={field.id}
            handleChange={this.onInputChange}
            value={value}
            shortAddress={shortAddress}
            last={index === fieldsLen}
            index={index}
            handleKeyPress={this.onKeyPress}
            handleFocus={this.onFocus}
            handleBlur={this.onBlur}
          />
          {this.renderClearBtn(field, index)}
        </div>
      );
    });

    return inputs;
  }

  renderClearBtn(field, index) {
    const { fields } = this.props;
    const fieldsLen = fields.length - 1;

    let clearBtn;
    if ((index >= 2 || fieldsLen >= 2) && index !== 0) {
      clearBtn = <ClearBtn {...field} />;
    }

    return clearBtn;
  }

  render() {
    const {
      children,
      isMobile,
      directionsLoaded,
      editing,
      animationName,
      translateX,
      animationTimeout,
      showDestinationChargers,
    } = this.props;
    const { keyPressed } = this.state;

    let holdTime = editing ? 150 : 50000;
    if (isMobile && editing) holdTime = 300;

    let styles = {};
    if (animationName && !isMobile) {
      styles = {
        transform: `${translateX}`,
        animation: `${animationName} .${animationTimeout}s 1`,
      };
    }

    const placesRef = el => {
      this.component = el;
    };

    return (
      <div>
        <div
          style={styles}
          className={classNames({
            places: !editing,
            'places--editing': editing,
            'places--mobile places--slide-down':
              isMobile && directionsLoaded && !showDestinationChargers,
            'places--mobile places--slide-up':
              isMobile && showDestinationChargers,
          })}
          onClick={this.toggleEditMode}
          role="button"
          tabIndex="0"
          ref={placesRef}
        >
          <Reorder
            reorderId="orderable"
            onReorder={this.onReorder}
            lock="horizontal"
            holdTime={holdTime}
          >
            {this.renderInputs()}
          </Reorder>
          {children}
        </div>
        <Autocomplete keyPressed={keyPressed} />
      </div>
    );
  }
}

Places.propTypes = PlacesPropTypes;

Places.defaultProps = PlacesDefaultProps;

const mapStateToProps = state => ({
  mapLoaded: state.GoogleMap.mapLoaded,
  fields: state.Places.fields,
  isMobile: state.App.isMobile,
  directionsLoaded: state.Directions.directionsLoaded,
  currentState: state,
  editing: state.Places.editing,
  fieldsLength: state.Places.fieldsLength,
  stopoversSingular: state.Places.numberOfStopoversSingular,
  stopoversPlural: state.Places.numberOfStopoversPlural,
  superchargers: state.Superchargers.superchargers,
  showDestinationChargers: state.DestinationChargers.showDestinationChargers,
  animationName: state.DestinationChargers.animationName,
  translateX: state.DestinationChargers.translateX,
  animationTimeout: state.DestinationChargers.animationTimeout,
  routechargers: state.Superchargers.routechargers,
});

const mapDispatchToProps = dispatch => ({
  dispatchSetPlacesAddress: (field, address) => {
    dispatch(setPlacesAddress(field, address));
  },
  dispatchUpdateAutocompleteInput: input => {
    dispatch(updateAutocompleteInput(input));
  },
  dispatchSetPlacesFocused: target => {
    dispatch(setFocusedPlaces(target));
  },
  dispatchSetPlace: (field, place, fieldsLen) => {
    dispatch(setPlace(field, place, fieldsLen));
  },
  dispatchReorderPlacesFields: (field, prevIndex, nextIndex) => {
    dispatch(reorderPlacesFields(field, prevIndex, nextIndex));
  },
  dispatchEditPlaces: currentState => {
    dispatch(editPlaces(currentState));
  },
  dispatchErasePlace: field => {
    dispatch(erasePlace(field));
  },
  dispatchUpdateSuperchargersRoutechargers: routeChargers => {
    dispatch(updateSuperchargersRoutechargers(routeChargers));
  },
  dispatchComponentHeight: (component, height) => {
    dispatch(updateAppComponentHeight(component, height));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Places);
